import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import Lottie from "react-lottie";
import Chart from "react-apexcharts";
import Utils from "Utils/utils";
import BarChartLoader from "Utils/LottieFiles/BarChart.json";
import NoActivityFound from "../ReportsModule/NoActivityFound";
import linkedInAutomationState from "./state";

import { DATE_FILTER_LIST } from "../ReportsModule/constants";

const series = [
  {
    name: "Connections sent",
    data: []
  },
  {
    name: "Connections accepted",
    data: []
  },
  {
    name: "Messages sent",
    data: []
  },
  {
    name: "Replies received",
    data: []
  }
];

const options = {
  chart: {
    type: "bar",
    height: "100px",
    stacked: true,
    toolbar: { show: false },
    zoom: { enabled: false }
  },
  colors: ["#2F7EDA", "#1EB1A7", "#F49730", "#8051D6"],
  responsive: [
    {
      breakpoint: 480,
      options: {
        legend: {
          position: "bottom",
          offsetX: -10,
          offsetY: 0
        }
      }
    }
  ],
  yaxis: {
    labels: {
      formatter: value => Math.floor(value)
    }
  },
  dataLabels: { enabled: false },
  plotOptions: {
    bar: {
      borderRadius: 0,
      horizontal: false
    }
  },
  legend: {
    position: "top",
    horizontalAlign: "right",
    show: false
  },
  fill: { opacity: 1 }
};

const LinkedInAutomationInsights = observer(() => {
  const [chartData, setChartData] = useState(series);
  const [chartOptions, setChartOptions] = useState(options);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: BarChartLoader
  };

  const constructChartData = () => {
    const data = linkedInAutomationState?.linkedInInsightData;
    if (data) {
      const tempData = [...chartData];
      tempData.map(item => (item.data = []));
      const tempXaxisDate = [];

      if (data && Object.keys(data)?.length) {
        for (let item in data) {
          const {
            linkedinConnectionRequestsSent = 0,
            linkedinConnectionRequestsAccepted = 0,
            linkedInMessagesSent = 0,
            linkedinRepliesReceived = 0,
            activityDate = ""
          } = data?.[item];

          tempData[0].data.push(Math.ceil(linkedinConnectionRequestsSent) || 0);
          tempData[1].data.push(Math.ceil(linkedinConnectionRequestsAccepted) || 0);
          tempData[2].data.push(Math.ceil(linkedInMessagesSent) || 0);
          tempData[3].data.push(Math.ceil(linkedinRepliesReceived) || 0);
          tempXaxisDate.push(activityDate);
        }
      }

      const { filterFromDate, filterToDate, days } =
        linkedInAutomationState.filterPayload;

      let startDate = Utils.formatDateHypen(filterFromDate);
      let endDate = Utils.formatDateHypen(filterToDate);

      const hasStartDate = tempXaxisDate.indexOf(startDate) !== -1;
      const hasEndDate = tempXaxisDate.indexOf(endDate) !== -1;

      if (data && Object.keys(data)?.length && days > 1) {
        !hasStartDate && tempXaxisDate.push(startDate);
        !hasEndDate && tempXaxisDate.splice(0, 0, endDate);

        tempData.map(item => {
          !hasStartDate && item.data.push(0);
          !hasEndDate && item.data.splice(0, 0, 0);
        });
      }

      setChartOptions({
        ...options,
        xaxis: {
          type: days > 1 ? "datetime" : "category",
          categories:
            (days > 1 && tempXaxisDate) ||
            tempXaxisDate.map(item => Utils.formatDate(item)),
          labels: {
            datetimeFormatter: { month: "dd MMM" }
          }
        },
        plotOptions: {
          bar: {
            columnWidth: (days > 20 && "40%") || "20%"
          }
        }
      });

      setChartData(tempData);
      linkedInAutomationState.setLinkedInInsightLoader(false);
    }
  };

  useEffect(() => {
    constructChartData();
  }, [linkedInAutomationState?.linkedInInsightData]);

  const getTrendTitle = () => {
    const { value } = linkedInAutomationState.filterPayload;
    const selectedFilter = DATE_FILTER_LIST.find(item => item.value === value);
    const name = selectedFilter ? selectedFilter.title : "";
    return `${name} Performance Trend`;
  };

  const legendItems = [
    { name: "Connections sent", color: "#2F7EDA" },
    { name: "Connections accepted", color: "#1EB1A7" },
    { name: "Messages sent", color: "#F49730" },
    { name: "Replies received", color: "#8051D6" }
  ];

  return (
    <div className="linkedInInsightsWrapper">
      {!linkedInAutomationState?.linkedInInsightLoader &&
        chartData?.length > 0 &&
        chartData[0]?.data?.length > 0 && (
          <div className="stackedBarChart">
            <div className="linkedInInsightsHeader">
              <div className="trendTitle">{getTrendTitle()}</div>
              <div className="chartLegend">
                {legendItems.map((item, index) => (
                  <div className="legendItem" key={index}>
                    <span
                      className="legendColor"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="legendText">{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <Chart
              options={chartOptions}
              series={chartData}
              type="bar"
              height={300}
            />
          </div>
        )}

      {linkedInAutomationState?.linkedInInsightLoader && (
        <div className="chartLoader">
          <Lottie
            options={defaultOptions}
            width={200}
            height={250}
            style={{ margin: "0 auto" }}
            speed={1}
          />
        </div>
      )}

      {!linkedInAutomationState?.linkedInInsightLoader &&
        (!chartData?.length || !chartData[0]?.data?.length) && (
          <NoActivityFound />
        )}
    </div>
  );
});

export { LinkedInAutomationInsights };
export default LinkedInAutomationInsights;
